import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';

export interface FilterSelectProps<T> {
  title: string;
  fetchItems: (query: string) => Promise<T[]>;
  selected: number[];
  selectedItems: T[];
  onChange: (item: T) => void;
  getLabel: (item: T) => string;
  searchPlaceholder?: string;
}

export function FilterSelect<T extends { id: number }>(props: FilterSelectProps<T>) {
  const { title, fetchItems, selected, selectedItems, onChange, getLabel, searchPlaceholder } = props;
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchItems(searchText)
        .then((data) => setResults(data || []))
        .catch((err) => {
          console.error('Error fetching filter items:', err);
          setResults([]);
        });
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchText, fetchItems]);

  const combinedItems: T[] = [...results];
  selectedItems.forEach((selectedItem) => {
    if (!combinedItems.some((result) => result.id === selectedItem.id)) {
      combinedItems.push(selectedItem);
    }
  });

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <TextField
        fullWidth
        placeholder={searchPlaceholder || `Пошук ${title.toLowerCase()}...`}
        size="small"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{ mb: 1 }}
      />
      <List dense sx={{ borderBottom: '1px solid', borderColor: 'divider', maxHeight: '350px', overflow: 'auto' }}>
        {combinedItems.map((item) => (
          <ListItem
            key={item.id}
            component="div"
            onClick={() => onChange(item)}
            sx={{ cursor: 'pointer' }}
            className="filter-list-item"
          >
            <ListItemIcon sx={{ minWidth: 'unset' }}>
              <Checkbox
                sx={{ pb: 0, pt: 0 }}
                edge="start"
                checked={selected.includes(item.id)}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText primary={getLabel(item)} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
